import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import RichText from "@/components/brand/RichText";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import HexInput from "@/components/ui/hex-input";
import PmsInput from "@/components/ui/pms-input";
import { ColorPicker, isGradient } from "@/components/ui/color-picker";
import { useI18n } from "@/contexts/I18nContext";
import { Download, Upload, UploadCloud, Copy as CopyIcon, X, ChevronLeft, ChevronRight, ChevronDown, Trash2, Plus, Check, FileText as FileTextIcon, Pencil, PaintBucket, ArrowLeftRight, RotateCw } from "lucide-react";
import {
  LogoVariantBlock,
  LogoAvatarBlock,
  LogoClearSpaceBlock,
  LogoColorUsageBlock,
  LogoDontsBlock,
} from "@/components/brand/blocks/LogoBlocks";

const dashed = "bh-editable-block rounded-xl border border-dashed border-gray-200 p-5 focus-within:border-[var(--bh-accent,#fe7f57)] transition-colors";

export default function BlockRenderer({ block, editMode, brand, onUpdate, pageBlocks = [] }) {
  const c = block.content || {};
  switch (block.type) {
    case "heading":
      return editMode ? (
        <div className={dashed}>
          <input
            data-testid={`heading-input-${block.id}`}
            value={c.text || ""}
            onChange={(e) => onUpdate({ text: e.target.value })}
            className="w-full font-display text-3xl md:text-4xl font-medium tracking-tight bg-transparent border-none outline-none"
            placeholder="Titel"
          />
        </div>
      ) : (
        <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight my-2">{c.text}</h2>
      );

    case "text":
      return editMode ? (
        <div className={dashed}>
          <RichText
            testId={`text-input-${block.id}`}
            value={c.text || ""}
            onChange={(html) => onUpdate({ text: html })}
            placeholder="Schrijf hier je tekst…"
            className="w-full min-h-[80px] text-base leading-relaxed outline-none"
          />
        </div>
      ) : (
        <div className="text-[15px] md:text-base leading-relaxed text-[#1a1a1a] bh-rt-view" dangerouslySetInnerHTML={{ __html: c.text || "" }} />
      );

    case "title_text_2col":
      return (
        <div className="grid md:grid-cols-[1fr_1.6fr] gap-8 md:gap-16 py-4 items-start">
          {editMode ? (
            <div className={`${dashed} self-start`}>
              <input
                value={c.title || ""}
                onChange={(e) => onUpdate({ title: e.target.value })}
                className="w-full font-display text-xl md:text-[22px] font-medium tracking-tight bg-transparent border-none outline-none leading-7 p-0 h-7"
                placeholder="Kop"
              />
            </div>
          ) : (
            <h3 className="font-display text-xl md:text-[22px] font-medium tracking-tight text-[#1a1a1a] self-start">{c.title}</h3>
          )}
          {editMode ? (
            <div className={`${dashed} self-start`}>
              <RichText
                value={c.text || ""}
                onChange={(html) => onUpdate({ text: html })}
                placeholder="Uitleg…"
                className="min-h-7 text-[15px] leading-7 outline-none"
              />
            </div>
          ) : (
            <div className="text-[15px] md:text-base leading-relaxed text-[#1a1a1a] bh-rt-view" dangerouslySetInnerHTML={{ __html: c.text || "" }} />
          )}
        </div>
      );

    case "core_values":
      return <CoreValuesBlock block={block} editMode={editMode} onUpdate={onUpdate}
        titlePlaceholder="Kernwaarden"
        nameKey="settings.editor_cv_name_placeholder"
        textKey="settings.editor_cv_text_placeholder"
        addKey="settings.editor_add_core_value" />;

    case "characteristics":
      return <CoreValuesBlock block={block} editMode={editMode} onUpdate={onUpdate}
        titlePlaceholder="Karakteristieken"
        nameKey="settings.editor_char_name_placeholder"
        textKey="settings.editor_char_text_placeholder"
        addKey="settings.editor_add_characteristic" />;

    case "audience":
      return <CoreValuesBlock block={block} editMode={editMode} onUpdate={onUpdate}
        titlePlaceholder="Doelgroep"
        nameKey="settings.editor_audience_name_placeholder"
        textKey="settings.editor_audience_text_placeholder"
        addKey="settings.editor_add_audience" />;

    case "dos_donts":
      return <DosDontsBlock block={block} editMode={editMode} onUpdate={onUpdate} />;

    case "dictionary":
      return <DictionaryBlock block={block} editMode={editMode} onUpdate={onUpdate} />;

    case "image":
      return <MediaBlock c={c} editMode={editMode} brand={brand} onUpdate={onUpdate} />;

    case "image_hero":
      return <ImageHeroBlock c={c} editMode={editMode} brand={brand} onUpdate={onUpdate} />;

    case "image_2col":
      return (
        <div className="grid md:grid-cols-2 gap-6">
          <MediaBlock c={c.left || {}} editMode={editMode} brand={brand} onUpdate={(u) => onUpdate({ left: { ...(c.left || {}), ...u } })} />
          <MediaBlock c={c.right || {}} editMode={editMode} brand={brand} onUpdate={(u) => onUpdate({ right: { ...(c.right || {}), ...u } })} />
        </div>
      );

    case "button": {
      const styles = c.style === "secondary"
        ? "bg-white text-[#1a1a1a] border border-gray-300"
        : "bg-black text-white border border-black";
      return editMode ? (
        <div className={`${dashed} flex flex-wrap gap-2 items-center`}>
          <Input value={c.label || ""} onChange={(e) => onUpdate({ label: e.target.value })} className="max-w-xs bg-white" placeholder="Label" />
          <Input value={c.href || ""} onChange={(e) => onUpdate({ href: e.target.value })} className="max-w-md bg-white" placeholder="https://" />
          <select value={c.style || "primary"} onChange={(e) => onUpdate({ style: e.target.value })} className="bh-select h-10 px-3 border border-gray-300 rounded-md bg-white">
            <option value="primary">Primair</option>
            <option value="secondary">Secundair</option>
          </select>
        </div>
      ) : (
        <a href={c.href || "#"} target="_blank" rel="noreferrer" className={`inline-flex items-center h-10 px-5 rounded-full text-sm font-normal ${styles}`}>{c.label || "Knop"}</a>
      );
    }

    case "color_palette":
      return <ColorPaletteBlock c={c} editMode={editMode} onUpdate={onUpdate} />;

    case "image_grid":
      return <ImageGridBlock c={c} editMode={editMode} brand={brand} onUpdate={onUpdate} />;

    case "typography":
      return <TypographyBlock c={c} editMode={editMode} brand={brand} onUpdate={onUpdate} />;

    case "logo":
      return <LogoBlock c={c} editMode={editMode} brand={brand} onUpdate={onUpdate} />;

    case "logo_variant":
      return <LogoVariantBlock block={block} editMode={editMode} brand={brand} onUpdate={onUpdate} />;

    case "logo_avatar":
      return <LogoAvatarBlock block={block} editMode={editMode} brand={brand} onUpdate={onUpdate} />;

    case "logo_clear_space":
      return <LogoClearSpaceBlock block={block} editMode={editMode} pageBlocks={pageBlocks} onUpdate={onUpdate} />;

    case "logo_color_usage":
      return <LogoColorUsageBlock block={block} editMode={editMode} brand={brand} pageBlocks={pageBlocks} onUpdate={onUpdate} />;

    case "logo_donts":
      return <LogoDontsBlock block={block} editMode={editMode} brand={brand} pageBlocks={pageBlocks} onUpdate={onUpdate} />;

    case "download":
      return <DownloadBlock c={c} editMode={editMode} brand={brand} onUpdate={onUpdate} />;

    case "divider":
      return <hr className="my-6 border-t border-gray-200" />;

    default:
      return <div className="text-red-700">Onbekend blok: {block.type}</div>;
  }
}

function ImageHeroBlock({ c, editMode, brand, onUpdate }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/upload?brand_id=${brand.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUpdate({ file_id: data.file_id, content_type: data.content_type, filename: data.filename });
    } catch { toast.error("Uploaden mislukt"); }
    finally { setBusy(false); }
  };
  const src = c.file_id ? fileUrl(c.file_id) : null;
  const bg = c.bg_color || "#b7cff7";
  return (
    <div className={editMode ? "rounded-xl border-2 border-dashed border-gray-200 p-3" : "-mx-10"}>
      <div className="relative rounded-2xl overflow-hidden" style={{ background: bg }}>
        <div className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr] min-h-[380px] md:min-h-[460px]">
          <div className="relative">
            {src ? (
              <img src={src} alt={c.caption || ""} className="absolute inset-0 w-full h-full object-cover" />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-[#1a1a1a]/50 text-sm">
                Geen beeld — upload een afbeelding
              </div>
            )}
            {editMode && (
              <>
                <input ref={inputRef} type="file" hidden accept="image/*" onChange={(e) => upload(e.target.files?.[0])} />
                <button onClick={() => inputRef.current?.click()} disabled={busy} className="absolute top-4 left-4 z-10 flex items-center gap-1.5 px-3 h-9 rounded-full bg-white/95 border border-gray-300 text-xs font-normal">
                  <Upload className="w-3.5 h-3.5" /> {busy ? "Bezig…" : (src ? "Vervang" : "Upload")}
                </button>
              </>
            )}
          </div>
          <div className="flex flex-col justify-end p-8 md:p-10">
            {editMode ? (
              <>
                <textarea
                  value={c.caption || ""}
                  onChange={(e) => onUpdate({ caption: e.target.value })}
                  className="w-full bg-transparent border-none outline-none text-xl md:text-2xl font-display font-medium text-[#1a1a1a] placeholder:text-[#1a1a1a]/40 resize-none"
                  placeholder="Caption of titel voor dit beeld…"
                  rows={3}
                />
                <div className="mt-3 flex items-center gap-2">
                  <label className="text-xs text-[#1a1a1a]/60">Achtergrond</label>
                  <input type="color" value={bg} onChange={(e) => onUpdate({ bg_color: e.target.value })} className="w-7 h-7 rounded border border-gray-300" />
                </div>
              </>
            ) : (
              c.caption ? (
                <div className="font-display text-xl md:text-2xl font-medium tracking-tight text-[#1a1a1a]">{c.caption}</div>
              ) : null
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function MediaBlock({ c, editMode, brand, onUpdate }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/upload?brand_id=${brand.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUpdate({ file_id: data.file_id, content_type: data.content_type, filename: data.filename });
    } catch { toast.error("Uploaden mislukt"); }
    finally { setBusy(false); }
  };
  const src = c.file_id ? fileUrl(c.file_id) : null;
  const isVideo = (c.content_type || "").startsWith("video/");
  const wrapCls = editMode ? dashed : "";
  return (
    <div className={wrapCls}>
      <div className="relative rounded-xl overflow-hidden bg-gray-100 aspect-video flex items-center justify-center">
        {src ? (
          isVideo ? (
            <video src={src} controls className="w-full h-full object-cover" />
          ) : (
            <img src={src} alt={c.caption || ""} className="w-full h-full object-cover" />
          )
        ) : (
          <div className="text-gray-400 text-sm">Geen beeld</div>
        )}
        {editMode && (
          <>
            <input ref={inputRef} type="file" hidden accept="image/*,video/*" onChange={(e) => upload(e.target.files?.[0])} />
            <button onClick={() => inputRef.current?.click()} disabled={busy} className="absolute top-2 right-2 flex items-center gap-1.5 px-3 h-8 rounded-full bg-white/95 border border-gray-300 text-xs font-normal">
              <Upload className="w-3.5 h-3.5" /> {busy ? "Bezig…" : (src ? "Vervang" : "Upload")}
            </button>
          </>
        )}
      </div>
      {editMode ? (
        <Input value={c.caption || ""} onChange={(e) => onUpdate({ caption: e.target.value })} className="mt-3 bg-white text-sm" placeholder="Onderschrift (optioneel)" />
      ) : c.caption ? (
        <div className="mt-2 text-sm text-gray-500">{c.caption}</div>
      ) : null}
    </div>
  );
}

function ImageGridBlock({ c, editMode, brand, onUpdate }) {
  const items = c.items || [];
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef();
  const [lightboxIdx, setLightboxIdx] = useState(null);
  const viewableItems = items.filter((it) => it.file_id);

  useEffect(() => {
    if (editMode || lightboxIdx === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightboxIdx(null);
      else if (e.key === "ArrowRight") setLightboxIdx((n) => (n + 1) % viewableItems.length);
      else if (e.key === "ArrowLeft") setLightboxIdx((n) => (n - 1 + viewableItems.length) % viewableItems.length);
    };
    window.addEventListener("keydown", onKey);
    // Scroll-blocker: both body AND nested scroll containers (the dashboard
    // actually scrolls inside `.bh-scroll`, not on body).
    const scrollables = Array.from(document.querySelectorAll(".bh-scroll"));
    const prevBody = document.body.style.overflow;
    const prev = scrollables.map((el) => el.style.overflow);
    document.body.style.overflow = "hidden";
    scrollables.forEach((el) => { el.style.overflow = "hidden"; });
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevBody;
      scrollables.forEach((el, i) => { el.style.overflow = prev[i]; });
    };
  }, [editMode, lightboxIdx, viewableItems.length]);

  const uploadImages = async (files) => {
    const list = Array.from(files || []).filter((f) => f.type?.startsWith("image/"));
    if (!list.length || !brand?.id) return;
    setBusy(true);
    try {
      const added = [];
      for (const file of list) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post(`/upload?brand_id=${brand.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push({ id: crypto.randomUUID?.() || Math.random().toString(36).slice(2), file_id: data.file_id, caption: "" });
      }
      onUpdate({ items: [...items, ...added] });
      toast.success(added.length > 1 ? `${added.length} afbeeldingen toegevoegd` : "Afbeelding toegevoegd");
    } catch { toast.error("Uploaden mislukt"); }
    finally { setBusy(false); }
  };

  const updateItem = (i, patch) => {
    const next = [...items];
    next[i] = { ...next[i], ...patch };
    onUpdate({ items: next });
  };
  const moveItem = (i, dir) => {
    const t = i + dir;
    if (t < 0 || t >= items.length) return;
    const next = [...items];
    [next[i], next[t]] = [next[t], next[i]];
    onUpdate({ items: next });
  };
  const removeItem = (i) => onUpdate({ items: items.filter((_, j) => j !== i) });

  return (
    <div className="space-y-16">
      {/* Section header — same structure as "Sectietitel" block */}
      {(editMode || c.title || c.description) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div>
            {editMode ? (
              <input
                data-testid="imagegrid-title-input"
                value={c.title || ""}
                onChange={(e) => onUpdate({ title: e.target.value })}
                placeholder="Sectietitel"
                className="w-full text-4xl font-medium tracking-tight bg-transparent border-none outline-none focus:outline-none focus-visible:outline-none"
              />
            ) : (
              <h3 className="text-4xl font-medium tracking-tight text-[#1a1a1a]">{c.title}</h3>
            )}
          </div>
          <div>
            {editMode ? (
              <textarea
                data-testid="imagegrid-description-input"
                value={c.description || ""}
                onChange={(e) => onUpdate({ description: e.target.value })}
                placeholder="Beschrijving van deze sectie…"
                rows={3}
                className="w-full text-base text-[#1a1a1a] bg-transparent border border-dashed border-gray-200 focus:border-[var(--bh-accent,#fe7f57)] rounded-lg p-3 outline-none focus:outline-none focus-visible:outline-none resize-y min-h-[88px] transition-colors leading-relaxed"
              />
            ) : (
              c.description ? <p className="text-base leading-relaxed text-[#1a1a1a]">{c.description}</p> : null
            )}
          </div>
        </div>
      )}

      <div className="columns-1 md:columns-2 lg:columns-3 gap-4" data-testid="image-grid">
        {items.map((it, i) => (
          <div key={it.id || i} className={`break-inside-avoid mb-14 ${editMode ? "bg-[#f2f1ed] rounded-xl p-4" : ""} space-y-3`} data-testid={`image-grid-item-${i}`}>
            {editMode ? (
              <div className="rounded overflow-hidden flex items-center justify-center">
                {it.file_id ? (
                  <img src={fileUrl(it.file_id)} alt={it.caption || ""} className="w-full h-auto rounded" />
                ) : (
                  <div className="w-full aspect-[4/3] flex items-center justify-center text-sm text-gray-400">Geen beeld</div>
                )}
              </div>
            ) : (
              it.file_id ? (
                <button
                  type="button"
                  onClick={() => setLightboxIdx(viewableItems.findIndex((v) => (v.id || v.file_id) === (it.id || it.file_id)))}
                  data-testid={`image-lightbox-open-${i}`}
                  className="block w-full p-0 border-0 bg-transparent group focus:outline-none focus-visible:outline-none"
                  aria-label="Open afbeelding op volledig scherm"
                >
                  <img
                    src={fileUrl(it.file_id)}
                    alt={it.caption || ""}
                    className="w-full h-auto rounded cursor-zoom-in transition-opacity group-hover:opacity-90"
                  />
                </button>
              ) : null
            )}
            {editMode ? (
              <>
                <textarea
                  data-testid={`image-caption-${i}`}
                  value={it.caption || ""}
                  onChange={(e) => updateItem(i, { caption: e.target.value })}
                  placeholder="Beschrijf de richtlijnen voor deze afbeelding..."
                  rows={2}
                  className="w-full bg-white rounded-md px-3 py-2 border border-transparent focus:border-[var(--bh-accent,#fe7f57)] text-sm text-[#1a1a1a] placeholder:text-gray-400 resize-y min-h-[64px] focus:outline-none focus-visible:outline-none leading-relaxed transition-colors"
                />
                <div className="flex items-center gap-1 pt-1">
                  <button onClick={() => moveItem(i, -1)} disabled={i === 0} data-testid={`image-left-${i}`} title="Vorige positie" className="w-7 h-7 rounded-md bg-white hover:bg-gray-100 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed">
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => moveItem(i, 1)} disabled={i === items.length - 1} data-testid={`image-right-${i}`} title="Volgende positie" className="w-7 h-7 rounded-md bg-white hover:bg-gray-100 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed">
                    <ChevronRight className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => removeItem(i)} data-testid={`image-del-${i}`} title="Verwijder" className="w-7 h-7 rounded-md bg-white hover:bg-red-100 hover:text-red-700 flex items-center justify-center">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </>
            ) : (
              it.caption ? <p className="mt-2 text-sm text-gray-500 leading-relaxed">{it.caption}</p> : null
            )}
          </div>
        ))}
      </div>
      {editMode && (
        <div
          data-testid="add-images-dropzone"
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => { e.preventDefault(); setDrag(false); uploadImages(e.dataTransfer.files); }}
          onClick={() => inputRef.current?.click()}
          className={`mt-4 border-2 border-dashed ${drag ? "border-[var(--bh-accent,#fe7f57)] bg-white" : "border-gray-300 bg-gray-50"} flex flex-col items-center justify-center cursor-pointer min-h-[200px] rounded-xl transition-colors`}
        >
          <input ref={inputRef} type="file" hidden multiple accept="image/*" onChange={(e) => uploadImages(e.target.files)} />
          <UploadCloud className="w-10 h-10 text-gray-400 mb-3" strokeWidth={1.5} />
          <div className="text-sm text-[#1a1a1a] text-center px-6">
            {busy ? "Bezig…" : "Sleep afbeeldingen hierheen, of klik om te bladeren"}
          </div>
        </div>
      )}

      {!editMode && lightboxIdx !== null && viewableItems[lightboxIdx] && createPortal(
        <div
          data-testid="image-lightbox-modal"
          onClick={() => setLightboxIdx(null)}
          className="fixed inset-0 z-[100] backdrop-blur-xl bg-white/30 flex items-center justify-center p-6 md:p-12 animate-in fade-in duration-150"
          role="dialog"
          aria-modal="true"
        >
          <button
            onClick={(e) => { e.stopPropagation(); setLightboxIdx(null); }}
            data-testid="lightbox-close"
            className="absolute top-5 right-5 w-11 h-11 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-md transition-colors"
            aria-label="Sluit"
          >
            <X className="w-5 h-5 text-[#1a1a1a]" />
          </button>
          {viewableItems.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); setLightboxIdx((n) => (n - 1 + viewableItems.length) % viewableItems.length); }}
                data-testid="lightbox-prev"
                className="absolute left-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-md transition-colors"
                aria-label="Vorige afbeelding"
              >
                <ChevronLeft className="w-5 h-5 text-[#1a1a1a]" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); setLightboxIdx((n) => (n + 1) % viewableItems.length); }}
                data-testid="lightbox-next"
                className="absolute right-5 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-md transition-colors"
                aria-label="Volgende afbeelding"
              >
                <ChevronRight className="w-5 h-5 text-[#1a1a1a]" />
              </button>
            </>
          )}
          <figure
            className="max-w-[90vw] max-h-full flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={fileUrl(viewableItems[lightboxIdx].file_id)}
              alt={viewableItems[lightboxIdx].caption || ""}
              data-testid="lightbox-image"
              className="max-w-full max-h-[82vh] w-auto h-auto rounded-lg"
            />
            {viewableItems[lightboxIdx].caption && (
              <figcaption className="mt-4 text-sm text-gray-700 text-center max-w-[600px] leading-relaxed">
                {viewableItems[lightboxIdx].caption}
              </figcaption>
            )}
          </figure>
        </div>,
        document.body
      )}
    </div>
  );
}


function ColorPaletteBlock({ c, editMode, onUpdate }) {
  const colors = c.colors || [];
  const [copied, setCopied] = useState(null);

  const copy = (key, value) => {
    try {
      navigator.clipboard.writeText(value);
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    } catch { /* ignore */ }
  };

  const getContrast = (hex) => {
    // Gradients fall back to white — safer on multi-hue backgrounds.
    if (typeof hex === "string" && hex.trim().startsWith("linear-gradient")) return "#ffffff";
    const h = (hex || "").replace("#", "");
    if (h.length !== 6) return "#1a1a1a";
    const toLinear = (c) => {
      const v = parseInt(c, 16) / 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
    };
    const r = toLinear(h.substring(0, 2));
    const g = toLinear(h.substring(2, 4));
    const b = toLinear(h.substring(4, 6));
    const L = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    const whiteContrast = 1.05 / (L + 0.05);
    const blackContrast = (L + 0.05) / 0.05;
    return whiteContrast >= blackContrast ? "#ffffff" : "#1a1a1a";
  };

  const hexToRgb = (hex) => {
    const h = (hex || "").replace("#", "");
    if (h.length === 3) {
      return {
        r: String(parseInt(h[0] + h[0], 16)),
        g: String(parseInt(h[1] + h[1], 16)),
        b: String(parseInt(h[2] + h[2], 16)),
      };
    }
    if (h.length === 6) {
      return {
        r: String(parseInt(h.substring(0, 2), 16)),
        g: String(parseInt(h.substring(2, 4), 16)),
        b: String(parseInt(h.substring(4, 6), 16)),
      };
    }
    return null;
  };

  const parseRgb = (str) => {
    const nums = (str || "").match(/\d+/g) || [];
    return { r: nums[0] || "", g: nums[1] || "", b: nums[2] || "" };
  };
  const parseCmyk = (str) => {
    const nums = (str || "").match(/\d+/g) || [];
    return { c: nums[0] || "", m: nums[1] || "", y: nums[2] || "", k: nums[3] || "" };
  };
  const formatRgb = ({ r, g, b }) => (r || g || b ? `R${r} G${g} B${b}` : "");
  const formatCmyk = ({ c, m, y, k }) => (c || m || y || k ? `C${c} M${m} Y${y} K${k}` : "");

  const update = (i, patch) => {
    const next = [...colors];
    next[i] = { ...next[i], ...patch };
    onUpdate({ colors: next });
  };
  const updateRgbPart = (i, key, val) => {
    const parts = parseRgb(colors[i].rgb);
    parts[key] = (val || "").replace(/\D/g, "").slice(0, 3);
    update(i, { rgb: formatRgb(parts) });
  };
  const updateCmykPart = (i, key, val) => {
    const parts = parseCmyk(colors[i].cmyk);
    parts[key] = (val || "").replace(/\D/g, "").slice(0, 3);
    update(i, { cmyk: formatCmyk(parts) });
  };
  const updateHex = (i, raw) => {
    const clean = (raw || "").replace(/[^0-9a-fA-F]/g, "").slice(0, 6);
    const patch = { hex: clean ? `#${clean}` : "" };
    const rgb = hexToRgb(clean);
    if (rgb) patch.rgb = formatRgb(rgb);
    update(i, patch);
  };

  // ------- Gradient helpers -------
  const parseGradientCss = (css) => {
    const m = (css || "").match(/^(linear|radial)-gradient\((.+)\)\s*$/i);
    if (!m) return null;
    const gradient_type = m[1].toLowerCase();
    const inside = m[2].split(/,(?![^(]*\))/).map((s) => s.trim());
    let angle = 135;
    if (gradient_type === "linear" && /^-?\d+(\.\d+)?deg$/i.test(inside[0])) {
      angle = parseFloat(inside.shift());
    } else if (gradient_type === "radial" && !/#/.test(inside[0])) {
      inside.shift();
    }
    const hits = inside.map((p) => (p.match(/#[0-9a-fA-F]{3,8}/) || [])[0]).filter(Boolean);
    return {
      gradient_type,
      angle,
      from_hex: (hits[0] || "#ADD3FF").toUpperCase(),
      to_hex: (hits[hits.length - 1] || "#000000").toUpperCase(),
    };
  };

  const buildGradientCss = (gradient_type, angle, from_hex, to_hex) => {
    if (gradient_type === "radial") return `radial-gradient(circle, ${from_hex} 0%, ${to_hex} 100%)`;
    return `linear-gradient(${angle}deg, ${from_hex} 0%, ${to_hex} 100%)`;
  };

  // Normalize a color card so it always has `type`, `from`, `to`, etc.
  const normalize = (col) => {
    if (col?.type === "solid" || col?.type === "gradient") return col;
    const h = col?.hex || "";
    const isGrad = /^(linear|radial)-gradient/i.test(h);
    if (isGrad) {
      const parsed = parseGradientCss(h) || { gradient_type: "linear", angle: 135, from_hex: "#ADD3FF", to_hex: "#000000" };
      const fRgb = hexToRgb(parsed.from_hex);
      const tRgb = hexToRgb(parsed.to_hex);
      return {
        ...col,
        type: "gradient",
        gradient_type: parsed.gradient_type,
        angle: parsed.angle,
        from: { hex: parsed.from_hex, rgb: fRgb ? formatRgb(fRgb) : "", cmyk: "", pms: col.pms || "" },
        to:   { hex: parsed.to_hex,   rgb: tRgb ? formatRgb(tRgb) : "", cmyk: "", pms: "" },
      };
    }
    return { ...col, type: "solid" };
  };

  const updateGradient = (i, patch) => {
    const col = normalize(colors[i]);
    const next = { ...col, ...patch };
    next.hex = buildGradientCss(
      next.gradient_type || "linear",
      next.angle || 135,
      (next.from && next.from.hex) || "#ADD3FF",
      (next.to && next.to.hex) || "#000000",
    );
    update(i, next);
  };
  const updateStop = (i, side, patch) => {
    const col = normalize(colors[i]);
    const stop = { ...(col[side] || {}), ...patch };
    updateGradient(i, { [side]: stop });
  };
  const updateStopHex = (i, side, raw) => {
    const clean = (raw || "").replace(/[^0-9a-fA-F]/g, "").slice(0, 6);
    const hex = clean ? `#${clean.toUpperCase()}` : "";
    const rgb = hexToRgb(clean);
    updateStop(i, side, { hex, rgb: rgb ? formatRgb(rgb) : "" });
  };
  const updateStopRgbPart = (i, side, key, val) => {
    const col = normalize(colors[i]);
    const parts = parseRgb(col[side]?.rgb);
    parts[key] = (val || "").replace(/\D/g, "").slice(0, 3);
    updateStop(i, side, { rgb: formatRgb(parts) });
  };
  const updateStopCmykPart = (i, side, key, val) => {
    const col = normalize(colors[i]);
    const parts = parseCmyk(col[side]?.cmyk);
    parts[key] = (val || "").replace(/\D/g, "").slice(0, 3);
    updateStop(i, side, { cmyk: formatCmyk(parts) });
  };

  const setType = (i, type) => {
    const col = normalize(colors[i]);
    if (type === col.type) return;
    if (type === "solid") {
      const base = col.from || {};
      update(i, {
        type: "solid",
        hex: base.hex || col.hex || "#999999",
        rgb: base.rgb || "",
        cmyk: base.cmyk || "",
        pms: base.pms || col.pms || "",
        gradient_type: undefined, angle: undefined, from: undefined, to: undefined,
      });
    } else {
      const fromHex = col.hex && !/^(linear|radial)-gradient/i.test(col.hex) ? col.hex : "#ADD3FF";
      const fromRgb = hexToRgb(fromHex);
      const from = { hex: fromHex.toUpperCase(), rgb: col.rgb || (fromRgb ? formatRgb(fromRgb) : ""), cmyk: col.cmyk || "", pms: col.pms || "" };
      const to = { hex: "#000000", rgb: "R0 G0 B0", cmyk: "", pms: "" };
      update(i, {
        type: "gradient",
        gradient_type: "linear",
        angle: 135,
        from, to,
        hex: buildGradientCss("linear", 135, from.hex, to.hex),
      });
    }
  };

  const swapGradient = (i) => {
    const col = normalize(colors[i]);
    if (col.type !== "gradient") return;
    updateGradient(i, { from: col.to, to: col.from });
  };
  const rotateGradient = (i) => {
    const col = normalize(colors[i]);
    if (col.type !== "gradient" || col.gradient_type === "radial") return;
    updateGradient(i, { angle: ((col.angle || 135) + 45) % 360 });
  };
  const setGradientType = (i, gt) => {
    updateGradient(i, { gradient_type: gt });
  };

  const move = (i, dir) => {
    const t = i + dir;
    if (t < 0 || t >= colors.length) return;
    const next = [...colors];
    [next[i], next[t]] = [next[t], next[i]];
    onUpdate({ colors: next });
  };
  const duplicate = (i) => {
    const next = [...colors];
    next.splice(i + 1, 0, { ...colors[i] });
    onUpdate({ colors: next });
  };
  const remove = (i) => onUpdate({ colors: colors.filter((_, j) => j !== i) });
  const add = () => onUpdate({ colors: [...colors, { name: "Nieuwe kleur", type: "solid", hex: "#999999", rgb: "", cmyk: "", pms: "" }] });

  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 ${editMode ? "gap-6" : "gap-0"}`} data-testid="color-palette">
      {colors.map((rawCol, i) => {
        const col = normalize(rawCol);
        const txt = getContrast(col.hex);

        if (!editMode) {
          const renderStopValues = (stop, prefix) => {
            const rows = [
              { key: `${prefix}-hex`, label: "HEX", value: (stop.hex || "").toUpperCase() },
              { key: `${prefix}-rgb`, label: "RGB", value: stop.rgb || "" },
              { key: `${prefix}-cmyk`, label: "CMYK", value: stop.cmyk || "" },
              { key: `${prefix}-pms`, label: "PMS", value: stop.pms || "" },
            ];
            return (
              <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                {rows.map((r) => (
                  <div key={r.key} className="flex items-start gap-2 min-w-0">
                    <div className="min-w-0 flex-1">
                      <div className="text-[9px] uppercase tracking-widest opacity-60">{r.label}</div>
                      <div className="font-mono text-[12px] truncate">{r.value || "—"}</div>
                    </div>
                    {r.value && (
                      <button
                        onClick={() => copy(`${i}-${r.key}`, r.value)}
                        data-testid={`copy-${r.key}-${i}`}
                        className="mt-2.5 opacity-60 hover:opacity-100 transition-opacity shrink-0"
                        title={`Kopieer ${r.label}`}
                        style={{ color: txt }}
                      >
                        {copied === `${i}-${r.key}` ? <Check className="w-3.5 h-3.5" /> : <CopyIcon className="w-3.5 h-3.5" />}
                      </button>
                    )}
                  </div>
                ))}
              </div>
            );
          };

          return (
            <div key={i}>
              <div
                className="rounded-lg min-h-[400px] relative p-6 flex flex-col overflow-hidden"
                style={{ background: col.hex || "#eee", color: txt }}
                data-testid={`color-card-${i}`}
              >
                <div className="font-display text-2xl font-medium">{col.name || "—"}</div>

                {col.type === "gradient" ? (
                  <div className="mt-auto space-y-4" data-testid={`gradient-values-${i}`}>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest font-medium opacity-80 mb-2">From</div>
                      {renderStopValues(col.from || {}, "from")}
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-widest font-medium opacity-80 mb-2">To</div>
                      {renderStopValues(col.to || {}, "to")}
                    </div>
                  </div>
                ) : (
                  <div className="mt-auto grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                    {[
                      { key: "rgb", label: "RGB", value: col.rgb },
                      { key: "hex", label: "HEX", value: col.hex },
                      { key: "cmyk", label: "CMYK", value: col.cmyk },
                      { key: "pms", label: "PMS", value: col.pms },
                    ].map((code, idx) => (
                      <div key={code.key} className={`flex items-start gap-2 ${idx === 1 ? "order-3" : idx === 2 ? "order-2" : idx === 3 ? "order-4" : ""}`}>
                        <div className="min-w-0">
                          <div className="text-[10px] uppercase tracking-widest opacity-60">{code.label}</div>
                          <div className="font-mono text-[13px] truncate">{code.value || "—"}</div>
                        </div>
                        {code.value && (
                          <button
                            onClick={() => copy(`${i}-${code.key}`, code.value)}
                            data-testid={`copy-${code.key}-${i}`}
                            className="mt-3 opacity-60 hover:opacity-100 transition-opacity shrink-0"
                            title={`Kopieer ${code.label}`}
                            style={{ color: txt }}
                          >
                            {copied === `${i}-${code.key}` ? <Check className="w-3.5 h-3.5" /> : <CopyIcon className="w-3.5 h-3.5" />}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        }

        // -------- EDIT MODE --------
        const typeIsGrad = col.type === "gradient";
        const previewBg = typeIsGrad
          ? buildGradientCss(col.gradient_type || "linear", col.angle || 135, col.from?.hex || "#ADD3FF", col.to?.hex || "#000000")
          : (col.hex || "#999999");
        const solidRgb = parseRgb(col.rgb);
        const solidCmyk = parseCmyk(col.cmyk);
        const solidHexNoHash = (col.hex || "").replace("#", "");
        const fromStop = col.from || {};
        const toStop = col.to || {};
        const fromRgb = parseRgb(fromStop.rgb);
        const fromCmyk = parseCmyk(fromStop.cmyk);
        const toRgb = parseRgb(toStop.rgb);
        const toCmyk = parseCmyk(toStop.cmyk);
        const fromHexNoHash = (fromStop.hex || "").replace("#", "");
        const toHexNoHash = (toStop.hex || "").replace("#", "");

        return (
          <div key={i} className="rounded-xl bg-[#f3f3f1] p-5 space-y-4 bh-color-card">
            {/* NAME + TYPE row */}
            <div className="grid grid-cols-[1fr_120px] gap-3 items-end">
              <LabelField label="Name" value={col.name || ""} onChange={(v) => update(i, { name: v })} testid={`color-name-${i}`} />
              <div className="space-y-1">
                <div className="text-[10px] uppercase tracking-widest text-gray-500">Type</div>
                <select
                  data-testid={`color-type-${i}`}
                  value={col.type || "solid"}
                  onChange={(e) => setType(i, e.target.value)}
                  className="bh-select w-full h-10 bg-white border border-gray-200 rounded-md px-3 text-sm focus:outline-none"
                >
                  <option value="solid">Solid</option>
                  <option value="gradient">Gradient</option>
                </select>
              </div>
            </div>

            {/* Visual block — opens picker on click, bucket icon bottom-right */}
            <ColorPicker
              {...(typeIsGrad
                ? {
                    stops: { from: fromStop.hex || "#ADD3FF", to: toStop.hex || "#000000" },
                    onStopChange: (sideKey, v) => {
                      const rgb = hexToRgb((v || "").replace("#", ""));
                      updateStop(i, sideKey, { hex: v, rgb: rgb ? formatRgb(rgb) : "" });
                    },
                  }
                : {
                    value: col.hex || "#999999",
                    onChange: (v) => updateHex(i, v),
                  })}
              trigger={
                <div
                  className="relative rounded-lg h-[140px] overflow-hidden cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#1a1a1a]/30"
                  style={{ background: previewBg }}
                  data-testid={`color-card-${i}`}
                  role="button"
                  tabIndex={0}
                >
                  <div className="absolute bottom-3 right-3 w-9 h-9 rounded-md bg-white shadow-sm flex items-center justify-center pointer-events-none">
                    <PaintBucket className="w-4 h-4 text-[#1a1a1a]" />
                  </div>
                </div>
              }
            />

            {typeIsGrad ? (
              <>
                {/* Gradient TYPE + DEGREE row */}
                <div className="grid grid-cols-[1fr_auto] gap-6 items-end">
                  <div className="space-y-1">
                    <div className="text-[10px] uppercase tracking-widest text-gray-500">Type</div>
                    <select
                      data-testid={`gradient-type-${i}`}
                      value={col.gradient_type || "linear"}
                      onChange={(e) => setGradientType(i, e.target.value)}
                      className="bh-select w-full h-10 bg-white border border-gray-200 rounded-md px-3 text-sm focus:outline-none"
                    >
                      <option value="linear">Linear</option>
                      <option value="radial">Radial</option>
                    </select>
                  </div>
                  <div className="space-y-1">
                    <div className="text-[10px] uppercase tracking-widest text-gray-500">Degree</div>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => swapGradient(i)}
                        data-testid={`gradient-swap-${i}`}
                        title="Swap from/to"
                        className="w-10 h-10 rounded-md bg-white border border-gray-200 hover:bg-gray-50 flex items-center justify-center"
                      >
                        <ArrowLeftRight className="w-4 h-4 text-[#1a1a1a]" />
                      </button>
                      <button
                        type="button"
                        onClick={() => rotateGradient(i)}
                        disabled={col.gradient_type === "radial"}
                        data-testid={`gradient-rotate-${i}`}
                        title={`Rotate (${col.angle || 135}°)`}
                        className="w-10 h-10 rounded-md bg-white border border-gray-200 hover:bg-gray-50 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <RotateCw className="w-4 h-4 text-[#1a1a1a]" />
                      </button>
                    </div>
                  </div>
                </div>

                {/* FROM group */}
                <div className="space-y-2.5" data-testid={`gradient-from-${i}`}>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500">From</div>
                  <div className="grid grid-cols-2 gap-3 items-end">
                    <HexInput value={fromHexNoHash} onChange={(v) => updateStopHex(i, "from", v)} testid={`from-hex-${i}`} />
                    <PmsInput value={fromStop.pms || ""} onChange={(v) => updateStop(i, "from", { pms: v })} testid={`from-pms-${i}`} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="grid grid-cols-3 gap-1.5">
                      <PartField prefix="R" value={fromRgb.r} onChange={(v) => updateStopRgbPart(i, "from", "r", v)} testid={`from-rgb-r-${i}`} />
                      <PartField prefix="G" value={fromRgb.g} onChange={(v) => updateStopRgbPart(i, "from", "g", v)} testid={`from-rgb-g-${i}`} />
                      <PartField prefix="B" value={fromRgb.b} onChange={(v) => updateStopRgbPart(i, "from", "b", v)} testid={`from-rgb-b-${i}`} />
                    </div>
                    <div className="grid grid-cols-4 gap-1.5">
                      <PartField prefix="C" value={fromCmyk.c} onChange={(v) => updateStopCmykPart(i, "from", "c", v)} testid={`from-cmyk-c-${i}`} />
                      <PartField prefix="M" value={fromCmyk.m} onChange={(v) => updateStopCmykPart(i, "from", "m", v)} testid={`from-cmyk-m-${i}`} />
                      <PartField prefix="Y" value={fromCmyk.y} onChange={(v) => updateStopCmykPart(i, "from", "y", v)} testid={`from-cmyk-y-${i}`} />
                      <PartField prefix="K" value={fromCmyk.k} onChange={(v) => updateStopCmykPart(i, "from", "k", v)} testid={`from-cmyk-k-${i}`} />
                    </div>
                  </div>
                </div>

                {/* TO group */}
                <div className="space-y-2.5" data-testid={`gradient-to-${i}`}>
                  <div className="text-[10px] uppercase tracking-widest text-gray-500">To</div>
                  <div className="grid grid-cols-2 gap-3 items-end">
                    <HexInput value={toHexNoHash} onChange={(v) => updateStopHex(i, "to", v)} testid={`to-hex-${i}`} />
                    <PmsInput value={toStop.pms || ""} onChange={(v) => updateStop(i, "to", { pms: v })} testid={`to-pms-${i}`} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="grid grid-cols-3 gap-1.5">
                      <PartField prefix="R" value={toRgb.r} onChange={(v) => updateStopRgbPart(i, "to", "r", v)} testid={`to-rgb-r-${i}`} />
                      <PartField prefix="G" value={toRgb.g} onChange={(v) => updateStopRgbPart(i, "to", "g", v)} testid={`to-rgb-g-${i}`} />
                      <PartField prefix="B" value={toRgb.b} onChange={(v) => updateStopRgbPart(i, "to", "b", v)} testid={`to-rgb-b-${i}`} />
                    </div>
                    <div className="grid grid-cols-4 gap-1.5">
                      <PartField prefix="C" value={toCmyk.c} onChange={(v) => updateStopCmykPart(i, "to", "c", v)} testid={`to-cmyk-c-${i}`} />
                      <PartField prefix="M" value={toCmyk.m} onChange={(v) => updateStopCmykPart(i, "to", "m", v)} testid={`to-cmyk-m-${i}`} />
                      <PartField prefix="Y" value={toCmyk.y} onChange={(v) => updateStopCmykPart(i, "to", "y", v)} testid={`to-cmyk-y-${i}`} />
                      <PartField prefix="K" value={toCmyk.k} onChange={(v) => updateStopCmykPart(i, "to", "k", v)} testid={`to-cmyk-k-${i}`} />
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Solid HEX + PMS */}
                <div className="grid grid-cols-2 gap-3 items-end">
                  <HexInput value={solidHexNoHash} onChange={(v) => updateHex(i, v)} testid={`hex-${i}`} />
                  <PmsInput value={col.pms || ""} onChange={(v) => update(i, { pms: v })} testid={`pms-${i}`} />
                </div>
                {/* Solid RGB + CMYK */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="grid grid-cols-3 gap-1.5">
                    <PartField prefix="R" value={solidRgb.r} onChange={(v) => updateRgbPart(i, "r", v)} testid={`rgb-r-${i}`} />
                    <PartField prefix="G" value={solidRgb.g} onChange={(v) => updateRgbPart(i, "g", v)} testid={`rgb-g-${i}`} />
                    <PartField prefix="B" value={solidRgb.b} onChange={(v) => updateRgbPart(i, "b", v)} testid={`rgb-b-${i}`} />
                  </div>
                  <div className="grid grid-cols-4 gap-1.5">
                    <PartField prefix="C" value={solidCmyk.c} onChange={(v) => updateCmykPart(i, "c", v)} testid={`cmyk-c-${i}`} />
                    <PartField prefix="M" value={solidCmyk.m} onChange={(v) => updateCmykPart(i, "m", v)} testid={`cmyk-m-${i}`} />
                    <PartField prefix="Y" value={solidCmyk.y} onChange={(v) => updateCmykPart(i, "y", v)} testid={`cmyk-y-${i}`} />
                    <PartField prefix="K" value={solidCmyk.k} onChange={(v) => updateCmykPart(i, "k", v)} testid={`cmyk-k-${i}`} />
                  </div>
                </div>
              </>
            )}

            {/* Compact action strip */}
            <div className="flex items-center gap-1 pt-2">
              <button data-testid={`color-left-${i}`} onClick={() => move(i, -1)} disabled={i === 0} title="Vorige positie" className="w-7 h-7 rounded-md bg-white hover:bg-gray-100 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed">
                <ChevronLeft className="w-3.5 h-3.5 text-gray-700" />
              </button>
              <button data-testid={`color-right-${i}`} onClick={() => move(i, 1)} disabled={i === colors.length - 1} title="Volgende positie" className="w-7 h-7 rounded-md bg-white hover:bg-gray-100 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed">
                <ChevronRight className="w-3.5 h-3.5 text-gray-700" />
              </button>
              <button data-testid={`color-dup-${i}`} onClick={() => duplicate(i)} title="Dupliceer" className="w-7 h-7 rounded-md bg-white hover:bg-gray-100 flex items-center justify-center">
                <CopyIcon className="w-3.5 h-3.5 text-gray-700" />
              </button>
              <button data-testid={`color-del-${i}`} onClick={() => remove(i)} title="Verwijder" className="w-7 h-7 rounded-md bg-white hover:bg-red-100 hover:text-red-700 flex items-center justify-center">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        );
      })}
      {editMode && (
        <button
          data-testid="add-color-btn"
          onClick={add}
          className="rounded-xl min-h-[250px] border border-dashed border-gray-300 bg-transparent hover:border-gray-500 hover:bg-[#f3f3f1] flex items-center justify-center transition-colors"
          title="Kleur toevoegen"
        >
          <Plus className="w-10 h-10 text-gray-400" strokeWidth={1.5} />
        </button>
      )}
    </div>
  );
}

function LabelField({ label, value, onChange, testid }) {
  return (
    <label className="block cursor-text">
      <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1 select-none">{label}</div>
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white rounded-md px-3 py-2 text-sm border border-transparent focus:border-[var(--bh-accent,#fe7f57)] focus:outline-none focus-visible:outline-none transition-colors"
      />
    </label>
  );
}

function PartField({ prefix, value, onChange, testid }) {
  return (
    <label className="relative block bg-white rounded-md border border-transparent focus-within:border-[var(--bh-accent,#fe7f57)] transition-colors cursor-text h-12">
      <span className="absolute top-1 left-1.5 text-[10px] font-mono text-gray-400 select-none pointer-events-none uppercase">{prefix}</span>
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        inputMode="numeric"
        className="w-full h-full pt-3.5 pb-1 px-2 text-sm bg-transparent font-mono text-center focus:outline-none focus-visible:outline-none"
      />
    </label>
  );
}

function HexField_DEPRECATED({ value, onChange, testid }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => { setDraft(value); }, [value]);

  const handleChange = (raw) => {
    const clean = (raw || "").replace(/[^0-9a-fA-F]/g, "").slice(0, 6);
    setDraft(clean);
    // Only commit to parent (which drives the background) when a valid 3- or 6-char hex is present
    if (clean.length === 3 || clean.length === 6) {
      onChange(clean);
    } else if (clean.length === 0) {
      onChange("");
    }
  };

  return (
    <label className="block cursor-text">
      <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1 select-none">HEX</div>
      <div className="flex items-center bg-white rounded-md px-3 border border-transparent focus-within:border-[var(--bh-accent,#fe7f57)] transition-colors">
        <span className="text-sm font-mono text-gray-500 select-none">#</span>
        <input
          data-testid={testid}
          value={draft}
          onChange={(e) => handleChange(e.target.value)}
          className="flex-1 min-w-0 py-2 pl-1 text-sm bg-transparent font-mono uppercase focus:outline-none focus-visible:outline-none"
          maxLength={6}
        />
      </div>
    </label>
  );
}

function TypographyBlock({ c, editMode, brand, onUpdate }) {
  const { t } = useI18n();
  const styles = c.styles || [];
  const fontFiles = c.font_files || [];
  const primary = fontFiles[0];
  const customFamily = primary?.file_id ? `bh-font-${primary.file_id}` : null;
  const fontFamily = customFamily
    ? `"${customFamily}", "${c.family || ""}", ui-sans-serif, system-ui`
    : (c.family ? `"${c.family}", ui-sans-serif, system-ui` : "ui-sans-serif, system-ui");
  const [busy, setBusy] = useState(false);
  const [drag, setDrag] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const inputRef = useRef();

  // Inject @font-face for the primary font so preview and style cards render in it
  useEffect(() => {
    if (!primary?.file_id) return;
    const styleEl = document.createElement("style");
    styleEl.setAttribute("data-bh-font", primary.file_id);
    styleEl.textContent = `@font-face { font-family: 'bh-font-${primary.file_id}'; src: url('${fileUrl(primary.file_id)}'); font-display: swap; }`;
    document.head.appendChild(styleEl);
    return () => styleEl.remove();
  }, [primary?.file_id]);

  const stripExt = (name) => (name || "").replace(/\.[a-zA-Z0-9]+$/, "");
  const extOf = (name) => {
    const m = /\.([a-zA-Z0-9]+)$/.exec(name || "");
    return (m ? m[1] : "").toLowerCase();
  };

  const uploadFonts = async (files) => {
    const list = Array.from(files || []);
    if (!list.length || !brand?.id) return;
    setBusy(true);
    try {
      const added = [];
      for (const file of list) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post(`/upload?brand_id=${brand.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push({ file_id: data.file_id, filename: data.filename, label: stripExt(data.filename), ext: extOf(data.filename) });
      }
      onUpdate({ font_files: [...fontFiles, ...added] });
      toast.success(added.length > 1 ? t("settings.editor_fonts_uploaded_many").replace("{n}", added.length) : t("settings.editor_fonts_uploaded_one"));
    } catch { toast.error(t("settings.editor_upload_failed")); }
    finally { setBusy(false); }
  };

  const updateFont = (i, patch) => {
    const next = [...fontFiles];
    next[i] = { ...next[i], ...patch };
    onUpdate({ font_files: next });
  };
  const removeFont = (i) => onUpdate({ font_files: fontFiles.filter((_, j) => j !== i) });
  const downloadName = (f) => `${f.label || stripExt(f.filename) || f.file_id}.${f.ext}`;

  const newStyleId = () => (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));
  const updateStyle = (i, patch) => {
    const next = [...styles];
    next[i] = { ...next[i], ...patch };
    onUpdate({ styles: next });
  };
  const moveStyle = (i, dir) => {
    const t = i + dir;
    if (t < 0 || t >= styles.length) return;
    const next = [...styles];
    [next[i], next[t]] = [next[t], next[i]];
    onUpdate({ styles: next });
  };
  const dupStyle = (i) => {
    const next = [...styles];
    next.splice(i + 1, 0, { ...styles[i], id: newStyleId() });
    onUpdate({ styles: next });
  };
  const delStyle = (i) => onUpdate({ styles: styles.filter((_, j) => j !== i) });
  const addStyle = () => onUpdate({ styles: [...styles, { id: newStyleId(), label: "Heading", description: "", sample: "The quick brown fox", line_height: 130, letter_spacing: 0, text_case: "none" }] });

  const zipHref = fontFiles.length > 1 && brand?.id
    ? `${(api.defaults.baseURL || "")}/brands/${brand.id}/fonts.zip?ids=${encodeURIComponent(fontFiles.map((f) => f.file_id).join(","))}`
    : null;

  return (
    <div className="space-y-10">
      {/* 2-col header */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          {editMode ? (
            <input
              data-testid="font-family-input"
              value={c.family || ""}
              onChange={(e) => onUpdate({ family: e.target.value })}
              className="w-full text-4xl font-medium tracking-tight bg-transparent border-none outline-none focus:outline-none focus-visible:outline-none"
              placeholder="Outfit"
              style={{ fontFamily }}
            />
          ) : (
            <h3 className="text-4xl font-medium tracking-tight text-[#1a1a1a]" style={{ fontFamily }} data-testid="font-family-name">
              {c.family || t("settings.editor_typeface_fallback")}
            </h3>
          )}
        </div>
        <div className="space-y-4">
          {editMode ? (
            <textarea
              data-testid="font-description-input"
              value={c.description || ""}
              onChange={(e) => onUpdate({ description: e.target.value })}
              placeholder={t("settings.editor_typeface_description_placeholder")}
              rows={3}
              className="w-full text-base text-[#1a1a1a] bg-transparent border border-dashed border-gray-200 focus:border-[var(--bh-accent,#fe7f57)] rounded-lg p-3 outline-none focus:outline-none focus-visible:outline-none resize-y min-h-[88px] transition-colors leading-relaxed"
            />
          ) : (
            <p className="text-base leading-relaxed text-[#1a1a1a]">{c.description || t("settings.editor_typeface_description_fallback")}</p>
          )}

          {/* Collapsible download — view mode only, smooth grid-rows animation */}
          {!editMode && fontFiles.length > 0 && (
            <div className="rounded-lg bg-[#f2f1ed] overflow-hidden" data-testid="font-download-collapsible">
              <button
                onClick={() => setDownloadOpen((v) => !v)}
                data-testid="font-download-toggle"
                className="w-full flex items-center justify-between px-4 py-3 text-left"
              >
                <span className="text-sm font-normal text-[#1a1a1a]">{t("settings.editor_download_typeface")}</span>
                <ChevronDown className={`w-4 h-4 text-[#1a1a1a] transition-transform duration-300 ${downloadOpen ? "rotate-180" : ""}`} />
              </button>
              <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${downloadOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                <div className="overflow-hidden">
                  <div className="px-4 pb-4 space-y-1">
                    {fontFiles.map((f, i) => (
                      <div key={f.file_id} className={`flex items-center justify-between py-2 ${fontFiles.length > 1 && i < fontFiles.length - 1 ? "border-b border-gray-200/70" : ""}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          <FileTextIcon className="w-4 h-4 text-gray-500 shrink-0" />
                          <span className="text-sm text-[#1a1a1a] truncate" data-testid={`font-file-name-${i}`}>{f.label || stripExt(f.filename) || f.filename}</span>
                          <span className="text-[9px] font-normal uppercase tracking-wider px-1.5 py-0.5 rounded bg-white text-gray-600 shrink-0">{f.ext}</span>
                        </div>
                        <a
                          href={fileUrl(f.file_id, { download: true })}
                          download={downloadName(f)}
                          data-testid={`font-download-${i}`}
                          title={`Download ${downloadName(f)}`}
                          className="w-8 h-8 rounded-lg bg-white hover:bg-black hover:text-white text-[#1a1a1a] flex items-center justify-center transition-colors shrink-0"
                        >
                          <Download className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    ))}
                    {zipHref && (
                      <a
                        href={zipHref}
                        data-testid="font-download-zip-btn"
                        className="mt-3 inline-flex items-center h-9 px-4 rounded-lg bg-black text-white text-sm font-normal hover:bg-black/90 transition-colors"
                      >
                        <Download className="w-3.5 h-3.5 mr-2" /> Download alles (.zip)
                      </a>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Upload zone + file manager — edit mode only, 2 columns */}
      {editMode && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div
            data-testid="font-dropzone"
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); uploadFonts(e.dataTransfer.files); }}
            onClick={() => inputRef.current?.click()}
            className={`border-2 border-dashed ${drag ? "border-[var(--bh-accent,#fe7f57)] bg-white" : "border-gray-300 bg-gray-50"} rounded-xl p-10 flex flex-col items-center justify-center cursor-pointer transition-colors min-h-[220px]`}
          >
            <input ref={inputRef} type="file" hidden multiple accept=".otf,.ttf,.woff,.woff2" onChange={(e) => uploadFonts(e.target.files)} />
            <UploadCloud className="w-10 h-10 text-gray-400 mb-3" strokeWidth={1.5} />
            <div className="text-sm text-[#1a1a1a] text-center">
              {busy ? "Bezig…" : "Sleep .otf, .ttf, .woff of .woff2 bestanden hierheen, of klik om te bladeren"}
            </div>
          </div>

          <div data-testid="font-file-manager" className="flex flex-col">
            <div className="text-[11px] uppercase tracking-widest text-gray-500 mb-2">Geüploade bestanden</div>
            {fontFiles.length === 0 ? (
              <div className="flex-1 rounded-xl border border-dashed border-gray-200 bg-transparent flex items-center justify-center text-sm text-gray-500 px-6 py-10 text-center min-h-[180px]">
                Nog geen bestanden geüpload — sleep een lettertype naar links om te beginnen.
              </div>
            ) : (
              <div className="space-y-2">
                {fontFiles.map((f, i) => (
                  <div key={f.file_id} className="flex items-center justify-between p-3 border-b border-gray-100 bg-white rounded-lg shadow-sm">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <FileTextIcon className="w-4 h-4 text-gray-500 shrink-0" />
                      <label className="flex items-center gap-1.5 min-w-0 flex-1 cursor-text group">
                        <input
                          data-testid={`font-file-rename-${i}`}
                          value={f.label ?? stripExt(f.filename) ?? ""}
                          onChange={(e) => updateFont(i, { label: e.target.value })}
                          placeholder="Bestandsnaam"
                          className="min-w-0 flex-1 text-sm text-[#1a1a1a] bg-transparent border border-transparent focus:border-[var(--bh-accent,#fe7f57)] rounded-md px-2 py-1 focus:outline-none focus-visible:outline-none transition-colors"
                        />
                        <Pencil className="w-3 h-3 text-gray-400 group-hover:text-gray-700 shrink-0" strokeWidth={2} />
                      </label>
                      <span className="text-[9px] font-normal uppercase tracking-wider px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 shrink-0">{f.ext}</span>
                    </div>
                    <button
                      data-testid={`font-remove-${i}`}
                      onClick={() => removeFont(i)}
                      title="Verwijder"
                      className="w-7 h-7 rounded-md bg-gray-50 hover:bg-red-100 hover:text-red-700 text-gray-700 flex items-center justify-center shrink-0 ml-2"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Huge preview — left-aligned, bigger, "AaBbCc" */}
      <div
        data-testid="font-huge-preview"
        className="w-full overflow-hidden text-left text-[#1a1a1a] py-6"
        style={{ fontFamily, fontSize: "clamp(5rem, 16vw, 14rem)", lineHeight: 1, fontWeight: 700, letterSpacing: "-0.03em" }}
      >
        AaBbCc
      </div>

      {/* Styles 2-col grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {styles.map((s, i) => {
          const textTransform = s.text_case && s.text_case !== "none" ? s.text_case : "none";
          return (
            <div key={s.id || i} className="bg-[#f2f1ed] p-8 rounded-2xl space-y-4 bh-color-card" data-testid={`style-card-${i}`}>
              {editMode ? (
                <>
                  <label className="block cursor-pointer">
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1 select-none">Type</div>
                    <select
                      data-testid={`style-type-${i}`}
                      value={s.label || "Heading"}
                      onChange={(e) => updateStyle(i, { label: e.target.value })}
                      className="bh-select w-full bg-white rounded-md px-3 py-2 text-sm border border-transparent focus:border-[var(--bh-accent,#fe7f57)] focus:outline-none focus-visible:outline-none transition-colors cursor-pointer"
                    >
                      <option value="Heading">{t("settings.editor_style_heading")}</option>
                      <option value="Body text">{t("settings.editor_style_body")}</option>
                      <option value="Quote">{t("settings.editor_style_quote")}</option>
                      <option value="Tag">{t("settings.editor_style_tag")}</option>
                    </select>
                  </label>
                  <LabelField label={t("settings.editor_style_description")} value={s.description || ""} onChange={(v) => updateStyle(i, { description: v })} testid={`style-desc-${i}`} />
                  <label className="block cursor-text">
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1 select-none">{t("settings.editor_style_sample")}</div>
                    <textarea
                      data-testid={`style-sample-${i}`}
                      value={s.sample || ""}
                      onChange={(e) => updateStyle(i, { sample: e.target.value })}
                      rows={2}
                      className="w-full bg-white rounded-md px-3 py-2 text-sm border border-transparent focus:border-[var(--bh-accent,#fe7f57)] focus:outline-none focus-visible:outline-none transition-colors resize-y min-h-[64px]"
                    />
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <LabelField label={t("settings.editor_style_line_height")} value={String(s.line_height ?? "")} onChange={(v) => updateStyle(i, { line_height: parseInt((v || "").replace(/\D/g, ""), 10) || 0 })} testid={`style-lh-${i}`} />
                    <LabelField label={t("settings.editor_style_letter_spacing")} value={String(s.letter_spacing ?? "")} onChange={(v) => updateStyle(i, { letter_spacing: parseFloat((v || "").replace(/[^0-9.-]/g, "")) || 0 })} testid={`style-ls-${i}`} />
                  </div>
                  <label className="block cursor-pointer">
                    <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-1 select-none">{t("settings.editor_style_case")}</div>
                    <select
                      data-testid={`style-case-${i}`}
                      value={s.text_case || "none"}
                      onChange={(e) => updateStyle(i, { text_case: e.target.value })}
                      className="bh-select w-full bg-white rounded-md px-3 py-2 text-sm border border-transparent focus:border-[var(--bh-accent,#fe7f57)] focus:outline-none focus-visible:outline-none transition-colors cursor-pointer"
                    >
                      <option value="none">{t("settings.editor_case_none")}</option>
                      <option value="uppercase">UPPERCASE</option>
                      <option value="lowercase">lowercase</option>
                      <option value="capitalize">Capitalize</option>
                    </select>
                  </label>
                  <div className="pt-2 flex items-center gap-1">
                    <button onClick={() => moveStyle(i, -1)} disabled={i === 0} data-testid={`style-left-${i}`} title="Vorige positie" className="w-7 h-7 rounded-md bg-white hover:bg-gray-100 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed">
                      <ChevronLeft className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => moveStyle(i, 1)} disabled={i === styles.length - 1} data-testid={`style-right-${i}`} title="Volgende positie" className="w-7 h-7 rounded-md bg-white hover:bg-gray-100 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed">
                      <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => dupStyle(i)} data-testid={`style-dup-${i}`} title="Dupliceer" className="w-7 h-7 rounded-md bg-white hover:bg-gray-100 flex items-center justify-center">
                      <CopyIcon className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => delStyle(i)} data-testid={`style-del-${i}`} title="Verwijder" className="w-7 h-7 rounded-md bg-white hover:bg-red-100 hover:text-red-700 flex items-center justify-center">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="font-display text-2xl font-medium text-[#1a1a1a] mb-2">{s.label || t("settings.editor_style_fallback")}</div>
                  {s.description && <div className="text-sm text-gray-600 mb-4 leading-relaxed">{s.description}</div>}
                  <div
                    className="text-3xl md:text-4xl text-[#1a1a1a]"
                    style={{ fontFamily, lineHeight: `${s.line_height || 130}%`, letterSpacing: `${s.letter_spacing || 0}px`, textTransform }}
                  >
                    {s.sample || t("settings.editor_sample_fallback")}
                  </div>
                  <div className="text-sm text-[#1a1a1a] font-medium pt-2">
                    {t("settings.editor_line_height_label")}: {s.line_height || 130}%  ·  {t("settings.editor_letter_spacing_label")}: {s.letter_spacing || 0}px
                  </div>
                </>
              )}
            </div>
          );
        })}
        {editMode && (
          <button
            data-testid="add-style-btn"
            onClick={addStyle}
            className="border-2 border-dashed border-gray-300 bg-transparent hover:bg-[#f2f1ed] flex flex-col items-center justify-center rounded-2xl min-h-[200px] cursor-pointer transition-colors gap-3"
            title="Stijl toevoegen"
          >
            <Plus className="w-10 h-10 text-gray-400" strokeWidth={1.5} />
            <span className="text-sm text-gray-500 font-medium">Style toevoegen</span>
          </button>
        )}
      </div>
    </div>
  );
}

function LogoBlock({ c, editMode, brand, onUpdate }) {
  const inputRef = useRef();
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/upload?brand_id=${brand.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUpdate({ file_id: data.file_id, filename: data.filename });
    } catch { toast.error("Uploaden mislukt"); }
  };
  return (
    <div className={editMode ? dashed : ""}>
      <div className="p-10 rounded-xl bg-gray-50 border border-gray-100 flex flex-col items-center justify-center min-h-[260px]">
        {c.file_id ? (
          <img src={fileUrl(c.file_id)} alt="logo" className="max-h-40 object-contain" />
        ) : editMode ? (
          <div className="text-gray-400 text-sm">Upload een logo om te starten</div>
        ) : null}
        {editMode ? (
          <div className="mt-4 flex items-center gap-2">
            <input ref={inputRef} type="file" hidden accept="image/*" onChange={(e) => upload(e.target.files?.[0])} />
            <Button onClick={() => inputRef.current?.click()} className="rounded-full h-9 bg-black text-white hover:bg-black/90"><Upload className="w-4 h-4 mr-2" />Upload logo</Button>
            <Input value={c.description || ""} onChange={(e) => onUpdate({ description: e.target.value })} className="max-w-xs bg-white" placeholder="Beschrijving" />
          </div>
        ) : (
          <>
            <div className="mt-4 text-sm text-gray-500">{c.description}</div>
            {c.file_id && (
              <a href={fileUrl(c.file_id, { download: true })} className="mt-3 inline-flex items-center gap-2 px-4 h-9 rounded-full bg-black text-white text-sm font-normal" data-testid="logo-download">
                <Download className="w-4 h-4" /> Download
              </a>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function DownloadBlock({ c, editMode, brand, onUpdate }) {
  const inputRef = useRef();
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post(`/upload?brand_id=${brand.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUpdate({ file_id: data.file_id, filename: data.filename, label: c.label || data.filename });
    } catch { toast.error("Uploaden mislukt"); }
  };
  return (
    <div className={editMode ? dashed : ""}>
      <div className="p-5 rounded-xl bg-gray-50 border border-gray-100 flex items-center gap-4">
        <div className="w-12 h-12 rounded-lg bg-white border border-gray-200 flex items-center justify-center">
          <Download className="w-5 h-5 text-[#1a1a1a]" />
        </div>
        <div className="flex-1 min-w-0">
          {editMode ? (
            <div className="space-y-1">
              <Input value={c.label || ""} onChange={(e) => onUpdate({ label: e.target.value })} className="bg-transparent border-none px-0 font-normal" placeholder="Label" />
              <Input value={c.description || ""} onChange={(e) => onUpdate({ description: e.target.value })} className="bg-transparent border-none px-0 text-sm text-gray-500" placeholder="Omschrijving" />
            </div>
          ) : (
            <>
              <div className="font-normal text-[#1a1a1a]">{c.label || "Downloadbestand"}</div>
              <div className="text-sm text-gray-500 truncate">{c.description || c.filename}</div>
            </>
          )}
        </div>
        {editMode ? (
          <>
            <input ref={inputRef} type="file" hidden onChange={(e) => upload(e.target.files?.[0])} />
            <Button onClick={() => inputRef.current?.click()} className="rounded-full h-9 bg-black text-white hover:bg-black/90"><Upload className="w-4 h-4 mr-1" />{c.file_id ? "Vervang" : "Upload"}</Button>
          </>
        ) : c.file_id ? (
          <a href={fileUrl(c.file_id, { download: true })} data-testid="download-asset-btn" className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-black text-white text-sm font-normal">
            <Download className="w-4 h-4" /> Download
          </a>
        ) : (
          <span className="text-xs text-gray-400">Geen bestand</span>
        )}
      </div>
    </div>
  );
}

// Core values block — two columns: left is the section title, right is a
// repeatable list of {name, text} items with an inline "+" to append more.
// Used by the "Merkstrategie" guided flow and also available in the Add-
// section drawer as "Kernwaarden".
function CoreValuesBlock({ block, editMode, onUpdate, titlePlaceholder = "Kernwaarden", nameKey = "settings.editor_cv_name_placeholder", textKey = "settings.editor_cv_text_placeholder", addKey = "settings.editor_add_core_value" }) {
  const c = block.content || {};
  const items = Array.isArray(c.items) ? c.items : [];
  const { t } = useI18n();

  const newItemId = () => (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));
  const updateItem = (i, patch) => {
    const next = items.map((it, idx) => (idx === i ? { ...it, ...patch } : it));
    onUpdate({ items: next });
  };
  const addItem = () => onUpdate({ items: [...items, { id: newItemId(), name: "", text: "" }] });
  const removeItem = (i) => onUpdate({ items: items.filter((_, idx) => idx !== i) });

  if (!editMode) {
    return (
      <div className="grid md:grid-cols-[1fr_1.6fr] gap-8 md:gap-16 py-4">
        <h3 className="font-display text-xl md:text-[22px] font-medium tracking-tight text-[#1a1a1a] self-start">
          {c.title}
        </h3>
        <div className="space-y-6">
          {items.map((it) => (
            <div key={it.id} data-testid={`core-value-view-${it.id}`}>
              {it.name && (
                <div className="font-display text-lg font-medium text-[#1a1a1a] mb-1">{it.name}</div>
              )}
              {it.text && (
                <div
                  className="text-[15px] md:text-base leading-relaxed text-[#1a1a1a] bh-rt-view"
                  dangerouslySetInnerHTML={{ __html: it.text }}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="grid md:grid-cols-[1fr_1.6fr] gap-8 md:gap-16 py-4">
      <div className={`${dashed} self-start`}>
        <input
          data-testid={`core-values-title-${block.id}`}
          value={c.title || ""}
          onChange={(e) => onUpdate({ title: e.target.value })}
          className="w-full font-display text-xl md:text-[22px] font-medium tracking-tight bg-transparent border-none outline-none"
          placeholder={titlePlaceholder}
        />
      </div>
      <div className="space-y-3">
        {items.map((it, i) => (
          <div key={it.id} className={dashed} data-testid={`core-value-item-${i}`}>
            <input
              data-testid={`core-value-name-${i}`}
              value={it.name || ""}
              onChange={(e) => updateItem(i, { name: e.target.value })}
              placeholder={t(nameKey)}
              className="w-full font-display text-base font-medium bg-transparent border-none outline-none mb-2"
            />
            <RichText
              value={it.text || ""}
              onChange={(html) => updateItem(i, { text: html })}
              placeholder={t(textKey)}
              className="min-h-[64px] text-[14px] leading-relaxed outline-none"
            />
            <div className="pt-2 flex justify-end">
              <button
                data-testid={`core-value-remove-${i}`}
                onClick={() => removeItem(i)}
                title="Verwijder"
                className="w-6 h-6 rounded-md bg-gray-100 hover:bg-red-100 hover:text-red-700 flex items-center justify-center"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </div>
        ))}
        <button
          data-testid={`core-value-add-${block.id}`}
          onClick={addItem}
          className="w-full py-5 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-gray-400 hover:text-gray-700 flex items-center justify-center gap-2 transition-colors"
        >
          <Plus className="w-4 h-4" /> {t(addKey)}
        </button>
      </div>
    </div>
  );
}


function DosDontsBlock({ block, editMode, onUpdate }) {
  const c = block.content || {};
  const dos = Array.isArray(c.dos) ? c.dos : [];
  const donts = Array.isArray(c.donts) ? c.donts : [];
  const newItemId = () => (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));

  const updateList = (key, i, text) => {
    const list = (key === "dos" ? dos : donts).map((it, idx) => (idx === i ? { ...it, text } : it));
    onUpdate({ [key]: list });
  };
  const addItem = (key) => {
    const list = [...(key === "dos" ? dos : donts), { id: newItemId(), text: "" }];
    onUpdate({ [key]: list });
  };
  const removeItem = (key, i) => {
    const list = (key === "dos" ? dos : donts).filter((_, idx) => idx !== i);
    onUpdate({ [key]: list });
  };

  const renderColumn = (key, icon, label, placeholder) => {
    const list = key === "dos" ? dos : donts;
    const IconCmp = icon;
    return (
      <div className="space-y-3">
        <h3 className="font-display text-xl md:text-[22px] font-medium tracking-tight text-[#1a1a1a]">{label}</h3>
        {editMode ? (
          <>
            <ul className="space-y-2">
              {list.map((it, i) => (
                <li key={it.id} className="flex items-start gap-3" data-testid={`${key}-item-${i}`}>
                  <IconCmp className="w-4 h-4 text-black mt-[10px] shrink-0" strokeWidth={2.25} />
                  <div className={`${dashed} flex-1 py-2.5 px-3`}>
                    <input
                      data-testid={`${key}-input-${i}`}
                      value={it.text || ""}
                      onChange={(e) => updateList(key, i, e.target.value)}
                      placeholder={placeholder}
                      className="w-full text-[15px] leading-relaxed bg-transparent border-none outline-none"
                    />
                  </div>
                  <button
                    data-testid={`${key}-remove-${i}`}
                    onClick={() => removeItem(key, i)}
                    title="Verwijder"
                    className="mt-1.5 w-6 h-6 rounded-md bg-gray-100 hover:bg-red-100 hover:text-red-700 flex items-center justify-center shrink-0"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </li>
              ))}
            </ul>
            <button
              data-testid={`${key}-add-${block.id}`}
              onClick={() => addItem(key)}
              className="w-full py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-gray-400 hover:text-gray-700 flex items-center justify-center gap-2 transition-colors"
            >
              <Plus className="w-4 h-4" /> Toevoegen
            </button>
          </>
        ) : (
          <ul className="space-y-2">
            {list.filter((it) => (it.text || "").trim()).map((it) => (
              <li key={it.id} className="flex items-start gap-3" data-testid={`${key}-view-${it.id}`}>
                <IconCmp className="w-4 h-4 text-black mt-[6px] shrink-0" strokeWidth={2.25} />
                <span className="text-[15px] md:text-base leading-relaxed text-[#1a1a1a]">{it.text}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  return (
    <div className="grid md:grid-cols-2 gap-8 md:gap-16 py-4">
      {renderColumn("dos", Check, "Do's", "Wel doen…")}
      {renderColumn("donts", X, "Don'ts", "Niet doen…")}
    </div>
  );
}

function DictionaryBlock({ block, editMode, onUpdate }) {
  const c = block.content || {};
  const useWords = Array.isArray(c.use_words) ? c.use_words : [];
  const avoidWords = Array.isArray(c.avoid_words) ? c.avoid_words : [];
  const examples = Array.isArray(c.examples) ? c.examples : [];
  const newItemId = () => (crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2));

  const listFor = (key) => (key === "use_words" ? useWords : key === "avoid_words" ? avoidWords : examples);
  const updateList = (key, i, text) => {
    const next = listFor(key).map((it, idx) => (idx === i ? { ...it, text } : it));
    onUpdate({ [key]: next });
  };
  const addItem = (key) => onUpdate({ [key]: [...listFor(key), { id: newItemId(), text: "" }] });
  const removeItem = (key, i) => onUpdate({ [key]: listFor(key).filter((_, idx) => idx !== i) });

  const renderList = (key, label, placeholder, isTextarea) => {
    const list = listFor(key);
    return (
      <div className="space-y-2" data-testid={`dictionary-list-${key}`}>
        <div className="font-display text-base font-medium text-[#1a1a1a]">{label}</div>
        {editMode ? (
          <>
            <ul className="space-y-2">
              {list.map((it, i) => (
                <li key={it.id} className="flex items-start gap-2" data-testid={`${key}-item-${i}`}>
                  <div className={`${dashed} flex-1 py-2.5 px-3`}>
                    {isTextarea ? (
                      <textarea
                        data-testid={`${key}-input-${i}`}
                        value={it.text || ""}
                        onChange={(e) => updateList(key, i, e.target.value)}
                        placeholder={placeholder}
                        rows={2}
                        className="w-full text-[15px] leading-relaxed bg-transparent border-none outline-none resize-y"
                      />
                    ) : (
                      <input
                        data-testid={`${key}-input-${i}`}
                        value={it.text || ""}
                        onChange={(e) => updateList(key, i, e.target.value)}
                        placeholder={placeholder}
                        className="w-full text-[15px] leading-relaxed bg-transparent border-none outline-none"
                      />
                    )}
                  </div>
                  <button
                    data-testid={`${key}-remove-${i}`}
                    onClick={() => removeItem(key, i)}
                    title="Verwijder"
                    className="mt-1.5 w-6 h-6 rounded-md bg-gray-100 hover:bg-red-100 hover:text-red-700 flex items-center justify-center shrink-0"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </li>
              ))}
            </ul>
            <button
              data-testid={`${key}-add-${block.id}`}
              onClick={() => addItem(key)}
              className="w-full py-2.5 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-gray-400 hover:text-gray-700 flex items-center justify-center gap-2 transition-colors"
            >
              <Plus className="w-4 h-4" /> Toevoegen
            </button>
          </>
        ) : (
          <ul className="space-y-1.5">
            {list.filter((it) => (it.text || "").trim()).map((it) => (
              <li key={it.id} className="text-[15px] md:text-base leading-relaxed text-[#1a1a1a]" data-testid={`${key}-view-${it.id}`}>
                {it.text}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  };

  return (
    <div className="grid md:grid-cols-[1fr_1.6fr] gap-8 md:gap-16 py-4">
      {editMode ? (
        <div className={`${dashed} self-start`}>
          <input
            data-testid={`dictionary-title-${block.id}`}
            value={c.title || ""}
            onChange={(e) => onUpdate({ title: e.target.value })}
            className="w-full font-display text-xl md:text-[22px] font-medium tracking-tight bg-transparent border-none outline-none"
            placeholder="Woordenboek"
          />
        </div>
      ) : (
        <h3 className="font-display text-xl md:text-[22px] font-medium tracking-tight text-[#1a1a1a] self-start">{c.title}</h3>
      )}
      <div className="space-y-6">
        {renderList("use_words", "Deze woorden wel", "Woord of frase…", false)}
        {renderList("avoid_words", "Deze woorden niet", "Woord of frase…", false)}
        {renderList("examples", "Voorbeeldzinnen", "Voorbeeldzin…", true)}
      </div>
    </div>
  );
}

